/**
 * Full-page view for error boundary when error has a status (e.g. 401, 403).
 * Provides Logout and Home actions.
 */
import { Button } from "@material-ui/core";
import HomeIcon from "@material-ui/icons/Home";
import { useAuth } from "@qpr/client-library";
import clsx from "clsx";
import { useAppContext } from "../../../context/AppCtxProvider";
import { makeThemedStyles } from "../../../viewcomponents/utils";
import { t } from "i18next";
import React from "react";
import { Link, useParams } from "react-router-dom";
import type { ErrorWithStatusProps } from "../types";

const useStyles = makeThemedStyles("ErrorBoundary.Status", (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(226.09deg, #DCE7E9 -5.75%, rgba(255, 255, 255, 0) 74.52%), linear-gradient(220.4deg, #C3D7DA 14.33%, #E3E1EA 49.58%, #D5E6E7 78.35%)",
    height: "100vh",
    width: "100vw",
    position: "relative",
  },
  link: {
    lineHeight: 1,
    fontSize: "1.6em",
  },
  message: {
    fontSize: "3rem",
  },
  logout: {
    fontSize: "1.82em",
    position: "absolute",
    top: "1.25em",
    right: "3.12em",
    textDecoration: "underline",
    textTransform: "unset",
  },
  logoutRtl: {
    right: "unset",
    left: "3.12em",
  },
  icon: {
    marginLeft: "8px",
    marginRight: "-4px",
  },
}));

export const ErrorWithStatusView: React.FC<ErrorWithStatusProps> = ({ message }) => {
  const { tenantSymbol } = useParams<{ tenantSymbol: string }>();
  const classes = useStyles();
  const { logout } = useAuth();
  const { model, rtl } = useAppContext();

  return (
    <div className={classes.root}>
      <Button
        size="large"
        className={clsx(classes.logout, rtl && classes.logoutRtl)}
        variant="text"
        onClick={logout}
      >
        {t("Logout")}
      </Button>
      <h1 className={classes.message}>{message}</h1>
      <Button
        component={Link}
        classes={{
          root: classes.link,
          startIcon: rtl ? classes.icon : undefined,
        }}
        to={`/${tenantSymbol}/${model}/landingpage`}
        startIcon={<HomeIcon />}
        variant="contained"
        color="primary"
        size="large"
      >
        {t("Home")}
      </Button>
    </div>
  );
};

export default ErrorWithStatusView;
